import React, { useCallback, useMemo, useState } from "react";
import { Alert, Button, Card, Form, FormControl, FormGroup, FormLabel } from "react-bootstrap";
import { FormattedMessage, MessageDescriptor, useIntl } from "react-intl";
import Selector from "../../../components/selector";
import {
  NON_REFUNDABLE_PAYMENTS,
  PAYMENT_FORM_ACTIONS,
  PAYMENT_TYPES,
} from "../../witadmin/Common/constants";
import { OperationOption, TransactionDto } from "../index";
import { StyledCard, StyledSpinner } from "../styles";
import { ConfirmModal } from "./ConfirmModal";
import { PaymentFormContainer } from "./styles";
import { TransactionList } from "./TransactionList";

export const COMPLETE_REFUND_PAYMENTS = [PAYMENT_TYPES.SEPTEO_PAYMENTS];

const createMessage = <T extends Record<string, Record<string, MessageDescriptor>>>(messages: T) =>
  messages;

const messages = createMessage({
  modal: {
    title: {
      id: "reservationDetail.payment.modal.title",
      defaultMessage: "Warning",
    },
    ok: {
      id: "reservationDetail.payment.modal.ok",
      defaultMessage: "Confirm",
    },
    cancel: {
      id: "reservationDetail.payment.modal.cancel",
      defaultMessage: "Cancel",
    },
    description: {
      id: "reservationDetail.payment.modal.description",
      defaultMessage: "Are you sure to do a refund?. Once done it cannot go back.",
    },
  },
  payment: {
    title: {
      id: "reservationDetail.payment.title",
      defaultMessage: "Make payment / refund of {reservationId}",
    },
    titleNotRefund: {
      id: "reservationDetail.payment.titleNotRefund",
      defaultMessage: "Make payment of {reservationId}",
    },
    transactionType: {
      id: "reservationDetail.payment.transactionType",
      defaultMessage: "Transaction type",
    },
    selectTransactionType: {
      id: "reservationDetail.payment.selectTransactionType",
      defaultMessage: "Select a transaction type",
    },
    amount: {
      id: "reservationDetail.payment.amount",
      defaultMessage: "Amount",
    },
    description: {
      id: "reservationDetail.payment.description",
      defaultMessage: "Description",
    },
    confirmTransaction: {
      id: "reservationDetail.payment.confirmTransaction",
      defaultMessage: "Confirm transaction",
    },
    required: {
      id: "reservationDetail.payment.required",
      defaultMessage: "Required",
    },
    transactionId: {
      id: "reservationDetail.payment.transactionId",
      defaultMessage: "Transaction ID",
    },
    processing: {
      id: "reservationDetail.payment.processing",
      defaultMessage: "Processing...",
    },
    transactionIdHelperText: {
      id: "reservationDetail.payment.transactionIdHelperText",
      defaultMessage: "Enter a charge or authorization transaction ID.",
    },
    chargeAmountHelperText: {
      id: "reservationDetail.payment.chargeAmountHelperText",
      defaultMessage: "The amount to be refunded may not exceed the available amount",
    },
    invalidRefundTransaction: {
      id: "reservationDetail.payment.invalidRefundTransaction",
      defaultMessage: "This transaction is not available to refund",
    },
    noRefundableAmount: {
      id: "reservationDetail.payment.noRefundableAmount",
      defaultMessage: "No refundable amount available for this reservation",
    },
    invalidChargeAmountHelperText: {
      id: "reservationDetail.payment.invalidChargeAmountHelperText",
      defaultMessage: "The amount should be {value} {currency} at maximum.",
    },
    refundChargeAmountAvailableHelperText: {
      id: "reservationDetail.payment.refundChargeAmountAvailableHelperText",
      defaultMessage: "The max available amount to refund is {availableRefundAmount} {currency}",
    },
  },
  errors: {
    getPaymentOrderError: {
      id: "reservationDetail.transactionList.getPaymentOrderError",
      defaultMessage: "Error on getting payment order",
    },
  },
});

export interface PaymentFormProps {
  reservationId: string;
  operations: OperationOption[];
  transactions: TransactionDto[];
  transactionAmount: number;
  setTransactionAmount: (value: number) => void;
  transactionDescription: string;
  transactionId?: string;
  operationSelected?: OperationOption;
  onChangeAmount: (amount: number) => void;
  onChangeDescription: (description: string) => void;
  onChangeTransactionId: (transactionId?: string) => void;
  onSelectOperation: (event: any) => void;
  onConfirmTransaction: () => void;
  onConfirmRefund: () => void;
  setIsProcessing: (props: boolean) => void;
  isProcessing: boolean;
  modalOpen: boolean;
  setModalOpen: (isOpen: boolean) => void;
  isAlertHidden: boolean;
  alertVariant: "success" | "danger";
  alertMessage?: MessageDescriptor;
  pendingAmount: number;
  totalAmount: number;
  totalNetChargedAmount: number;
  isNewPayment?: string;
  isLoadingTransactions: boolean;
  resetForm: () => void;
  showOperationsForm: boolean;
  paymentProvider?: string;
}

export function PaymentForm({
  reservationId,
  operations,
  transactions,
  transactionAmount,
  setTransactionAmount,
  transactionDescription,
  transactionId,
  operationSelected,
  onChangeAmount,
  onChangeDescription,
  onChangeTransactionId,
  onSelectOperation,
  onConfirmTransaction,
  onConfirmRefund,
  isProcessing,
  modalOpen,
  setModalOpen,
  isAlertHidden,
  alertVariant,
  alertMessage,
  isLoadingTransactions,
  resetForm,
  showOperationsForm,
  paymentProvider,
  totalNetChargedAmount,
}: PaymentFormProps) {
  const intl = useIntl();
  const [isInvalidTransaction, setIsInvalidTransaction] = useState<boolean>(false);
  const [isInvalidAmount, setIsInvalidAmount] = useState<boolean>(false);

  const dontHaveTransactions = useMemo(() => transactions.length === 0, [transactions]);

  const firstTransactionIsNotOk = useMemo(() => {
    return (
      transactions
        .filter(it => it.status === "OK")
        .slice(-1)
        .pop()?.status !== "OK"
    );
  }, [transactions]);

  const isRefund = useMemo(() => operationSelected?.value === PAYMENT_FORM_ACTIONS.REFUND, [
    operationSelected?.value,
  ]);

  const transactionIdIsDefined = useMemo(() => !!transactionId && transactionId.length > 0, [
    transactionId,
  ]);

  const isTransactionRefunded = useMemo(() => {
    if (paymentProvider && !COMPLETE_REFUND_PAYMENTS.includes(paymentProvider)) {
      return false;
    }

    const selectedTransaction = transactions.find(it => it.id === transactionId);
    if (!selectedTransaction) {
      return false;
    }

    return transactions.some(
      it => it.refundTransactionId === selectedTransaction.id && it.status === "OK"
    );
  }, [paymentProvider, transactions, transactionId]);

  const isInvalidForm = useMemo(
    () => isInvalidTransaction || isInvalidAmount || isTransactionRefunded,
    [isInvalidAmount, isInvalidTransaction, isTransactionRefunded]
  );

  const isDisabledForm = useMemo(() => {
    const isInvalidAmount = transactionAmount <= 0;
    const dontHaveDescription = transactionDescription.length === 0;
    const dontHaveTransactionOrderNumber = transactionId?.length === 0;
    return (
      isInvalidForm ||
      firstTransactionIsNotOk ||
      !operationSelected ||
      isInvalidAmount ||
      dontHaveTransactions ||
      (!isRefund && dontHaveDescription) ||
      (isRefund && dontHaveTransactionOrderNumber) ||
      isTransactionRefunded
    );
  }, [
    transactionAmount,
    transactionDescription.length,
    transactionId?.length,
    operationSelected,
    isInvalidForm,
    dontHaveTransactions,
    isRefund,
    firstTransactionIsNotOk,
    isTransactionRefunded,
  ]);

  const transactionOrderNumber = useMemo(() => {
    return transactions.find(it => it.id === transactionId)?.orderNumber;
  }, [transactionId, transactions]);

  const availableTransactionRefundAmount = useMemo(() => {
    if (paymentProvider && COMPLETE_REFUND_PAYMENTS.includes(paymentProvider)) {
      return transactionAmount;
    }

    const transactionAvailable = transactions
      .filter(it => it.orderNumber === transactionOrderNumber)
      .filter(it => it.status === "OK")
      .map(it => {
        return it.type === "REFUND" ? it.total * -1 : it.total;
      })
      .reduce((acc, currentValue) => acc + currentValue, 0);

    return Math.round(Math.min(transactionAvailable, totalNetChargedAmount) * 100) / 100;
  }, [
    transactions,
    transactionOrderNumber,
    paymentProvider,
    transactionAmount,
    totalNetChargedAmount,
  ]);

  const isFullyRefunded = useMemo(() => isRefund && totalNetChargedAmount <= 0, [
    isRefund,
    totalNetChargedAmount,
  ]);

  const isAmountDisabled = useMemo(() => {
    if (isRefund && paymentProvider && COMPLETE_REFUND_PAYMENTS.includes(paymentProvider)) {
      return true;
    }
    return (
      isFullyRefunded ||
      isInvalidTransaction ||
      !operationSelected ||
      dontHaveTransactions ||
      isTransactionRefunded
    );
  }, [
    isRefund,
    isFullyRefunded,
    isInvalidTransaction,
    dontHaveTransactions,
    paymentProvider,
    operationSelected,
    isTransactionRefunded,
  ]);

  const isValidRefundTransaction = useCallback(
    (id: string) => {
      const selectedTransaction = transactions.find(it => it.id === id);
      return (
        selectedTransaction &&
        selectedTransaction?.type !== "REFUND" &&
        selectedTransaction?.status === "OK"
      );
    },
    [transactions]
  );

  const handleAmountChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = event.target.value.replace(",", ".");
      const finalAmount = isNaN(parseFloat(inputValue)) ? 0 : parseFloat(inputValue);
      setTransactionAmount(finalAmount);
      setIsInvalidAmount(isRefund && finalAmount > availableTransactionRefundAmount);
      onChangeAmount(finalAmount);
    },
    [availableTransactionRefundAmount, isRefund, setTransactionAmount, onChangeAmount]
  );

  const hasNonRefundableTransaction = useMemo(
    () => paymentProvider && NON_REFUNDABLE_PAYMENTS.includes(paymentProvider),
    [paymentProvider]
  );

  const handleChangeTransaction = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setIsInvalidTransaction(!isValidRefundTransaction(event.target.value));
      onChangeTransactionId(event.target.value);

      if (paymentProvider && COMPLETE_REFUND_PAYMENTS.includes(paymentProvider)) {
        const selectedTransaction = transactions.find(it => it.id === event.target.value);
        if (!selectedTransaction) {
          return;
        }

        setTransactionAmount(selectedTransaction.total);
        onChangeAmount(selectedTransaction.total);
      }
    },
    [
      paymentProvider,
      transactions,
      isValidRefundTransaction,
      onChangeTransactionId,
      setTransactionAmount,
      onChangeAmount,
    ]
  );

  return (
    <StyledCard className="ml-4 mr-4 mb-4 mt-4">
      <Card.Body>
        {showOperationsForm && (
          <div>
            <div className="border-bottom pb-2 mb-3 text-[21px]">
              {intl.formatMessage(
                messages.payment[hasNonRefundableTransaction ? "titleNotRefund" : "title"],
                {
                  reservationId: reservationId,
                }
              )}
            </div>
            <div className="mb-4 w-25">
              <PaymentFormContainer noValidate>
                <FormGroup>
                  <FormLabel className="font-weight-bold">
                    <FormattedMessage {...messages.payment.transactionType} />
                  </FormLabel>
                  <Selector
                    options={operations}
                    value={operationSelected}
                    onChange={(event: any) => {
                      resetForm();
                      onSelectOperation(event);
                    }}
                    placeholder={<FormattedMessage {...messages.payment.selectTransactionType} />}
                    className="w-100"
                    isDisabled={dontHaveTransactions || firstTransactionIsNotOk}
                    required
                  />
                </FormGroup>
                {isFullyRefunded && (
                  <Form.Text className="text-danger d-block mb-3">
                    <FormattedMessage {...messages.payment.noRefundableAmount} />
                  </Form.Text>
                )}
                {isRefund && (
                  <FormGroup controlId="validationOperationTransactionId">
                    <FormLabel className="font-weight-bold">
                      <FormattedMessage {...messages.payment.transactionId} />
                    </FormLabel>
                    <FormControl
                      className="rounded-right w-100"
                      type="text"
                      placeholder={intl.formatMessage(messages.payment.required)}
                      onChange={handleChangeTransaction}
                      value={transactionId}
                      isInvalid={isInvalidTransaction || isTransactionRefunded}
                      disabled={isFullyRefunded || !operationSelected || dontHaveTransactions}
                      required={isRefund}
                    />
                    <Form.Control.Feedback type="invalid">
                      <FormattedMessage {...messages.payment.invalidRefundTransaction} />
                    </Form.Control.Feedback>
                    <Form.Text muted hidden={transactionIdIsDefined}>
                      <FormattedMessage {...messages.payment.transactionIdHelperText} />
                    </Form.Text>
                    <Form.Text
                      muted
                      hidden={
                        isInvalidTransaction || !transactionIdIsDefined || isTransactionRefunded
                      }>
                      <FormattedMessage
                        {...messages.payment.refundChargeAmountAvailableHelperText}
                        values={{
                          availableRefundAmount: availableTransactionRefundAmount,
                          currency: "EUR",
                        }}
                      />
                    </Form.Text>
                  </FormGroup>
                )}
                <FormGroup controlId="validationOperationAmount">
                  <FormLabel className="font-weight-bold">
                    <FormattedMessage {...messages.payment.amount} />
                  </FormLabel>
                  <FormControl
                    type="number"
                    className="rounded-right w-100"
                    onChange={handleAmountChange}
                    disabled={isAmountDisabled}
                    max={availableTransactionRefundAmount}
                    value={transactionAmount}
                    min={0}
                    step={0.5}
                    required
                    isInvalid={transactionAmount > 0 && isInvalidAmount}
                  />
                  <Form.Control.Feedback type="invalid" hidden={!isRefund}>
                    <FormattedMessage
                      {...messages.payment.invalidChargeAmountHelperText}
                      values={{ value: availableTransactionRefundAmount, currency: "EUR" }}
                    />
                  </Form.Control.Feedback>
                  <Form.Text muted hidden={!isRefund}>
                    <FormattedMessage {...messages.payment.chargeAmountHelperText} />
                  </Form.Text>
                </FormGroup>
                {!isRefund && (
                  <FormGroup>
                    <FormLabel className="font-weight-bold">
                      <FormattedMessage {...messages.payment.description} />
                    </FormLabel>
                    <FormControl
                      type="text"
                      className="w-100"
                      placeholder={intl.formatMessage(messages.payment.required)}
                      onChange={event => onChangeDescription(event.target.value)}
                      disabled={isInvalidForm || !operationSelected || dontHaveTransactions}
                      value={transactionDescription}
                      required
                    />
                  </FormGroup>
                )}
              </PaymentFormContainer>
              <ConfirmModal
                isOpen={modalOpen}
                title={intl.formatMessage(messages.modal.title)}
                onClose={() => setModalOpen(false)}
                isProcessing={isProcessing}
                labelOk={intl.formatMessage(messages.modal.ok)}
                labelCancel={intl.formatMessage(messages.modal.cancel)}
                onConfirm={onConfirmRefund}>
                <p>
                  <FormattedMessage {...messages.modal.description} />
                </p>
              </ConfirmModal>
            </div>
            <div className="mb-5">
              <Button
                className="btn btn-default"
                disabled={isDisabledForm || isProcessing}
                type="submit"
                onClick={onConfirmTransaction}>
                {isProcessing ? (
                  <>
                    <StyledSpinner className="fa fa-spinner fa-spin mr-2" />
                    <FormattedMessage {...messages.payment.processing} />
                  </>
                ) : (
                  <FormattedMessage {...messages.payment.confirmTransaction} />
                )}
              </Button>
              <Alert hidden={isAlertHidden} variant={alertVariant} className="mt-3 w-50">
                {alertMessage && <FormattedMessage {...alertMessage} />}
              </Alert>
            </div>
          </div>
        )}
        <TransactionList
          data={transactions}
          isLoading={isLoadingTransactions}
          paymentType={paymentProvider}
          summarizeTotal={totalNetChargedAmount}
        />
      </Card.Body>
    </StyledCard>
  );
}
